import React, { useCallback, useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

// Material UI Icons
import FilterListIcon from '@mui/icons-material/FilterList';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SearchIcon from '@mui/icons-material/Search';

import Loading from '../../../components/global/loading/Loading';
import ReturnCard from '../../../components/global/returnCard/ReturnCard';
import { useReturn } from '../../../context/ReturnContext';
import {
  Return as ReturnType,
  ReturnStatus,
  translateReturnStatus,
} from '../../../models/Return';
import { Route, Routes } from 'react-router-dom';
import Return from '../../return/Return';
import { useDebounce } from '../../../hooks/useDebounce';

function Returns() {
  const { getAllReturns } = useReturn();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalReturns, setTotalReturns] = useState(0);
  const [returns, setReturns] = useState<ReturnType[]>([]);
  const [status, setStatus] = useState<ReturnStatus | 'all'>('all');
  const [limit, setLimit] = useState(10);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const getReturnsData = useCallback(
    async (
      page: number,
      status: ReturnStatus | undefined,
      limit: number,
      search?: string,
    ) => {
      setIsLoading(true);
      try {
        const { returns, totalPages, total } = await getAllReturns(
          page,
          limit,
          status,
          search,
        );
        setReturns(returns);
        setCurrentPage(page);
        setTotalPages(totalPages);
        setTotalReturns(total);
      } catch (error) {
        console.error('Error fetching returns:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [getAllReturns],
  );

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
    getReturnsData(
      1,
      status === 'all' ? undefined : status,
      limit,
      debouncedSearchTerm || undefined,
    );
  }, [status, limit, debouncedSearchTerm, getReturnsData]);

  useEffect(() => {
    getReturnsData(
      currentPage,
      status === 'all' ? undefined : status,
      limit,
      debouncedSearchTerm || undefined,
    );
  }, [currentPage, getReturnsData, status, limit, debouncedSearchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const clearFilters = () => {
    setStatus('all');
    setSearchTerm('');
  };

  const hasActiveFilters = status !== 'all' || debouncedSearchTerm !== '';

  return (
    <div className="flex flex-col items-stretch">
      <Routes>
        <Route
          path="/"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <AssignmentReturnIcon className="text-primary" />
                    Zarządzanie zwrotami
                  </h1>
                  <p className="text-gray-600 text-sm mt-1">
                    Panel administracyjny do zarządzania zwrotami klientów
                  </p>
                </motion.div>

                <motion.div
                  className="flex items-center gap-3 flex-wrap"
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  <TextField
                    placeholder="Szukaj zwrotu"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    className="min-w-[200px]"
                  />

                  <FormControl variant="outlined" size="small">
                    <InputLabel>Limit</InputLabel>
                    <Select
                      value={limit}
                      onChange={(e) => setLimit(Number(e.target.value))}
                      label="Limit"
                      className="w-24"
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>

                  <button
                    onClick={() => setIsFiltersVisible(!isFiltersVisible)}
                    className={`
                    flex items-center gap-1 py-2 px-3 rounded-md text-sm font-medium
                    ${
                      isFiltersVisible || hasActiveFilters
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                    transition-colors duration-200
                  `}
                  >
                    <FilterListIcon fontSize="small" />
                    <span>Filtry</span>
                    {hasActiveFilters && (
                      <span className="ml-1 bg-white text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                        {(status !== 'all' ? 1 : 0) +
                          (debouncedSearchTerm ? 1 : 0)}
                      </span>
                    )}
                  </button>
                </motion.div>
              </div>

              {/* Filters */}
              <AnimatePresence>
                {isFiltersVisible && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-medium text-gray-700">
                          Filtry wyszukiwania
                        </h3>
                        <button
                          onClick={clearFilters}
                          className="text-sm text-primary hover:underline flex items-center gap-1"
                          disabled={!hasActiveFilters}
                        >
                          <ClearIcon fontSize="small" />
                          Resetuj filtry
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Status zwrotu</InputLabel>
                          <Select
                            value={status}
                            onChange={(e) =>
                              setStatus(e.target.value as ReturnStatus | 'all')
                            }
                            label="Status zwrotu"
                          >
                            <MenuItem value="all">Wszystkie</MenuItem>
                            {Object.values(ReturnStatus).map((status) => (
                              <MenuItem key={status} value={status}>
                                {translateReturnStatus(status)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          label="Szukaj wg numeru zwrotu/zamówienia"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={searchTerm}
                          onChange={handleSearchChange}
                          placeholder="Wpisz numer zwrotu lub zamówienia"
                        />
                      </div>

                      <div className="mt-3 text-sm text-gray-600">
                        <p>
                          <strong>Wskazówka:</strong> Możesz wyszukiwać po
                          numerze zwrotu, numerze zamówienia lub nazwisku
                          klienta.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Content */}
              <AnimatePresence mode="wait">
                {isLoading ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    key="loading"
                    className="flex justify-center py-12"
                  >
                    <Loading />
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    key="content"
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {/* Status bar */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                      <div>
                        Wyświetlono{' '}
                        <span className="font-semibold">{returns.length}</span>{' '}
                        z <span className="font-semibold">{totalReturns}</span>{' '}
                        zwrotów
                      </div>
                      <div className="flex items-center gap-2">
                        <span>
                          Strona {currentPage} z {totalPages}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() =>
                              currentPage > 1 && setCurrentPage(currentPage - 1)
                            }
                            disabled={currentPage <= 1}
                            className={`p-1 rounded ${
                              currentPage <= 1
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <ArrowBackIosNewIcon
                              style={{ fontSize: '0.9rem' }}
                            />
                          </button>
                          <button
                            onClick={() =>
                              currentPage < totalPages &&
                              setCurrentPage(currentPage + 1)
                            }
                            disabled={currentPage >= totalPages}
                            className={`p-1 rounded ${
                              currentPage >= totalPages
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <ArrowForwardIosIcon
                              style={{ fontSize: '0.9rem' }}
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Return cards */}
                    {returns.length > 0 ? (
                      <div className="grid grid-cols-1 gap-4">
                        <AnimatePresence>
                          {returns.map((returnItem, index) => (
                            <motion.div
                              key={returnItem.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: 0.3,
                                delay: index * 0.05,
                              }}
                            >
                              <ReturnCard return={returnItem} />
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <AssignmentReturnIcon
                          style={{ fontSize: '3rem' }}
                          className="text-gray-300 mb-2"
                        />
                        <h3 className="text-lg font-medium text-gray-700">
                          Brak zwrotów
                        </h3>
                        <p className="text-gray-500 mt-1">
                          {hasActiveFilters
                            ? 'Nie znaleziono zwrotów spełniających kryteria wyszukiwania'
                            : 'Nie ma jeszcze żadnych zwrotów w systemie'}
                        </p>
                        {hasActiveFilters && (
                          <button
                            onClick={clearFilters}
                            className="mt-3 text-primary hover:underline"
                          >
                            Wyczyść filtry
                          </button>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          }
        />
        <Route path="/:id" element={<Return />} />
      </Routes>
    </div>
  );
}

export default Returns;
